import React, { useState } from 'react';
import styled from '@emotion/native';
import { List } from 'react-native-paper';
import { H3, H4 } from '@/shared/components/StyledText';
import { white, green, sand, accent } from '@/shared/styles/colors';
import useStore from '@/stores/useStore';
import { FAQ_DATA, FAQSection, FAQItem } from '@/shared/constants/faqData';
import FAQItemComponent from '@/components/help/FAQItem';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import ScrollableView from '@/shared/components/ScrollableView';

const HelpCenter = () => {
  const isStylist = useStore((state) => state.isStylist);
  const isDesktop = useIsDesktop();
  const scrollToTop = useScrollToTop();
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const renderFAQItem = (
    item: FAQItem,
    index: number,
    sectionTitle: string,
  ) => {
    const itemId = `${sectionTitle}-${index}`;
    const isExpanded = expandedSections[itemId];

    return (
      <FAQItemComponent
        key={itemId}
        item={item}
        itemId={itemId}
        isExpanded={isExpanded}
        onToggle={toggleSection}
      />
    );
  };

  const renderSection = (section: FAQSection, index: number) => {
    const itemsToShow = isStylist ? section.stylistItems : section.clientItems;

    if (!itemsToShow || itemsToShow.length === 0) {
      return null;
    }

    return (
      <SectionContainer key={`section-${index}`}>
        <List.Section>
          <SectionTitle>{section.title}</SectionTitle>
          {itemsToShow.map((item, itemIndex) =>
            renderFAQItem(item, itemIndex, section.title),
          )}
        </List.Section>
      </SectionContainer>
    );
  };

  return (
    <OuterContainer isDesktop={isDesktop}>
      <ScrollContainer showsVerticalScrollIndicator={false}>
        {!isDesktop && (
          <HeaderContainer>
            <StyledH3>Help Center</StyledH3>
          </HeaderContainer>
        )}

        <ContentContainer>
          {FAQ_DATA.map((section, index) => renderSection(section, index))}
        </ContentContainer>

        <BackToTopContainer>
          <BackToTopButton scrollToTop={scrollToTop} />
        </BackToTopContainer>
      </ScrollContainer>
    </OuterContainer>
  );
};

export default HelpCenter;

const OuterContainer = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flex: 1,
  backgroundColor: white,
  ...(isDesktop && {
    marginTop: 10,
    paddingHorizontal: 35,
    borderTopWidth: 1,
    borderTopColor: accent,
  }),
}));

const ScrollContainer = styled(ScrollableView)({
  flex: 1,
});

const HeaderContainer = styled.View({
  paddingTop: 35,
  paddingBottom: 25,
  alignItems: 'center',
  backgroundColor: sand,
});

const StyledH3 = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  color: green,
});

const ContentContainer = styled.View({
  paddingHorizontal: 16,
  paddingTop: 12,
});

const SectionContainer = styled.View({
  marginBottom: 12,
});

const SectionTitle = styled(H4)({
  color: green,
  fontSize: 24,
  fontWeight: '400' as const,
  marginBottom: 16,
  textTransform: 'uppercase',
});

const BackToTopContainer = styled.View({
  paddingTop: 32,
  paddingBottom: 24,
  paddingHorizontal: 16,
  alignItems: 'center',
});
